import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CalendarDaysIcon,
  UserGroupIcon,
  ChartBarSquareIcon,
  ChartPieIcon,
  WalletIcon,
  FolderPlusIcon,
  UserIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "../stores/authStore";
import { Switch } from "@/components/ui/switch";
import { MenuItem } from "@/stores";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const [isSidebarOpen, setSidebarOpen] = useState<boolean>(false);
  
  const sidebarRef = useRef<HTMLDivElement>(null);

  const menuItems: MenuItem[] = [
    {
      name: "Tableau de bord",
      path: "/jeuneInnovateur/tableauDeBord",
      icon: (
        <ChartBarSquareIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/tableauDeBord"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Mes projets",
      path: "/jeuneInnovateur/mesProjets/projetList",
      paths: [
        "/jeuneInnovateur/mesProjets/projetList",
        "/jeuneInnovateur/mesProjets/singleProjet",
        "/jeuneInnovateur/mesProjets/singleProjet/taches",
        "/jeuneInnovateur/mesProjets/singleProjet/discussions",
        "/jeuneInnovateur/mesProjets/singleProjet/membres",
        "/jeuneInnovateur/mesProjets/singleProjet/notifications",
        "/jeuneInnovateur/mesProjets/singleProjet/fichiers",
        "/jeuneInnovateur/mesProjets/singleProjet/integrations",
      ],
      icon: (
        <CalendarDaysIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/mesProjets/projetList" ||
            location.pathname === "/jeuneInnovateur/mesProjets/singleProjet"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Mentorat",
      path: "/jeuneInnovateur/mentorat",
      icon: (
        <UserGroupIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/mentorat"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Evenements",
      path: "/jeuneInnovateur/evenements",
      icon: (
        <WalletIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/evenements"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Forum",
      path: "/jeuneInnovateur/forum",
      icon: (
        <ChartPieIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/forum"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Formation",
      path: "/jeuneInnovateur/formation",
      icon: (
        <FolderPlusIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/formation"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Profil",
      path: "/jeuneInnovateur/profil",
      icon: (
        <UserIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/profil"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
    {
      name: "Aide",
      path: "/jeuneInnovateur/aide",
      icon: (
        <QuestionMarkCircleIcon
          className={`w-5 h-5 mr-2 ${
            location.pathname === "/jeuneInnovateur/aide"
              ? "text-rajapiBlue"
              : "text-rajapiGray"
          }`}
        />
      ),
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div>
      {/* Toggle button for mobile */}
      <button
        className="p-2 text-gray-500 lg:hidden"
        onClick={() => setSidebarOpen(!isSidebarOpen)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16m-7 6h7"
          />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`flex flex-col fixed inset-y-0 left-0 bg-white shadow-lg w-64 border-r transform h-full ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform lg:translate-x-0 lg:static lg:inset-0 min-h-screen z-10`}
      >
        <div className="flex p-5 text-center items-center">
          <img
            src="/images/RAJAPI_logo.png"
            alt="RAJAPI-COP"
            className="mx-0.5 size-16"
          />
          <span className="text-2xl font-serif text-rajapiBlue font-bold">
            RAJAPI-COP
          </span>
        </div>
        <nav className="flex-1 p-4">
          <ul>
            {menuItems.map((item) => (
              <li key={item.name} className="mb-4">
                <Link
                  to={item.paths ? item.paths[0] : item.path}
                  className={`flex py-2 px-4 rounded-lg items-center ${
                    (item.paths && item.paths.includes(location.pathname)) ||
                    location.pathname === item.path
                      ? "bg-gray-100 text-rajapiBlue border-2 border-gray-200 font-medium"
                      : "hover:bg-gray-100 text-gray-400"
                  }`}
                >
                  {item.icon}
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 ">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={`${
                  user?.profile_picture
                    ? user.profile_picture
                    : "/images/user.png"
                }`}
                alt="Profile"
                className="h-10 w-10 rounded-full"
              />
              <span className="ml-2 text-gray-800">
                {user && user?.first_name}
              </span>
            </div>
            <button className="text-gray-500 hover:text-gray-800">...</button>
          </div>
          <div className="mt-4 border-t">
            <label className="flex items-center space-x-2 p-5">
              <span className="text-sm text-gray-600">Dark Mode</span>
              <Switch />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
